/**
 * BdrHandoffQueue — W55 "BDR Handoff Queue"
 * W57: rewired to query mql_events via /api/marketing/handoff-queue instead of cadence_enrollments proxy.
 *
 * "Marketing-qualified leads waiting for BDR to claim or return."
 * Table: Lead, Company, Source, Generated, Status (NEW / IN PROGRESS)
 *
 * Data: mql_events where stage='MQL' AND bdr_status IN ('NEW','IN_PROGRESS').
 *
 * Tenant 1 only.
 */

import { useEffect, useState } from "react";
import { api, getCurrentTenant } from "@/lib/session";

const TENANT_ID = 1;

type HandoffStatus = "NEW" | "IN PROGRESS";

interface HandoffRow {
  id: number;
  lead: string;
  company: string;
  source: string;
  generated: string;
  status: HandoffStatus;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function mapBdrStatus(raw: string | null): HandoffStatus {
  if (raw === "IN_PROGRESS") return "IN PROGRESS";
  return "NEW";
}

export function BdrHandoffQueue() {
  const [rows, setRows] = useState<HandoffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tenant = getCurrentTenant();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api<{ items: Array<{
      id: number;
      lead_name: string | null;
      company_name: string | null;
      source: string | null;
      bdr_status: string | null;
      occurred_at: string | null;
    }> }>("/api/marketing/handoff-queue")
      .then((resp) => {
        if (cancelled) return;

        const handoffRows: HandoffRow[] = (resp?.items ?? []).map((r) => ({
          id: r.id,
          lead: r.lead_name ?? "Unknown",
          company: r.company_name ?? "—",
          source: r.source ?? "Inbound",
          generated: r.occurred_at ?? "",
          status: mapBdrStatus(r.bdr_status),
        }));

        setRows(handoffRows);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load handoff queue.");
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="h-4 w-48 bg-neutral-100 rounded mb-4" />
        {[1,2,3,4,5].map(i => <div key={i} className="h-10 w-full bg-neutral-100 rounded mb-1" />)}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        BDR Handoff Queue
      </div>
      <p className="mb-4 text-[11px] text-neutral-400">
        Marketing-qualified leads waiting for BDR to claim or return.
      </p>

      {error && (
        <p className="text-sm text-[#DC2626] mb-3">{error}</p>
      )}

      {rows.length === 0 && !error ? (
        <div className="py-6 text-center text-sm text-neutral-400">
          No leads in the BDR handoff queue. New MQL events will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] text-neutral-400">
              <tr className="border-b border-neutral-200">
                <th className="pb-2 font-medium">Lead</th>
                <th className="pb-2 font-medium">Company</th>
                <th className="pb-2 font-medium">Source</th>
                <th className="pb-2 font-medium">Generated</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-neutral-50">
                  <td className="py-2.5 font-medium text-neutral-900">{r.lead}</td>
                  <td className="py-2.5 text-neutral-600">{r.company}</td>
                  <td className="py-2.5 text-neutral-500 text-[11px]">{r.source}</td>
                  <td className="py-2.5 text-neutral-500 tabular-nums text-[11px]">
                    {formatDate(r.generated)}
                  </td>
                  <td className="py-2.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        r.status === "NEW"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-orange-50 text-orange-700"
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
